export const CANCEL_SUBSCRIPTION_REASONS = [
  {
    id: "didnt_mean_to",
    label: "I didn't mean to subscribe",
  },
  {
    id: "changed_my_mind",
    label: "I changed my mind",
  },
  {
    id: "just_wanted_to_try",
    label: "I just wanted to try it",
  },
  {
    id: "too_much_product",
    label: "I have too much product",
  },
  {
    id: "dont_use_as_much",
    label: "I don't use it as much",
  },
  {
    id: "cant_afford",
    label: "Can’t afford it right now",
  },
  {
    id: "prefer_to_order_on_own",
    label: "I prefer to order on my own",
  },
  {
    id: "other",
    label: "Other",
  },
] as const;

export type CancelSubscriptionReasonId =
  (typeof CANCEL_SUBSCRIPTION_REASONS)[number]["id"];

/** Whether to randomize reason order (keeps "Other" last). Off by default. */
export const CANCEL_REASONS_RANDOMIZE = false;

/** Reason IDs shown to pre-2nd-shipment customers. */
const PRE_SECOND_SHIPMENT_REASON_IDS: ReadonlySet<string> = new Set([
  "didnt_mean_to",
  "changed_my_mind",
  "just_wanted_to_try",
  "other",
]);

/** Reason IDs shown to customers past their 2nd shipment. */
const NORMAL_REASON_IDS: ReadonlySet<string> = new Set([
  "too_much_product",
  "dont_use_as_much",
  "cant_afford",
  "prefer_to_order_on_own",
  "other",
]);

export function getCancelReasonLabel(id: CancelSubscriptionReasonId): string {
  const found = CANCEL_SUBSCRIPTION_REASONS.find((o) => o.id === id);
  return found?.label ?? id;
}

/**
 * Returns reasons in display order.
 * - Pre-2nd shipment (orderCount missing or < 2): pre-2nd-shipment reason set.
 *   Treating `null` as pre-2nd is intentional — when data is missing we hide
 *   reasons that only make sense for established customers (e.g.
 *   "prefer_to_order_on_own"). Matches the `isBeforeSecondShipment` rule
 *   used elsewhere in the manage flow.
 * - Otherwise: normal reason set.
 * - When `isFlexibleSubscription` is true, hides "prefer_to_order_on_own"
 *   since the customer is already on flexible ordering.
 * Shuffles non-"other" entries when randomize is true.
 */
export function getDisplayReasons(
  randomize = CANCEL_REASONS_RANDOMIZE,
  orderCount?: number | null,
  isFlexibleSubscription = false,
): (typeof CANCEL_SUBSCRIPTION_REASONS)[number][] {
  const activeIds =
    orderCount == null || orderCount < 2
      ? PRE_SECOND_SHIPMENT_REASON_IDS
      : NORMAL_REASON_IDS;
  const reasons = CANCEL_SUBSCRIPTION_REASONS.filter(
    (r) =>
      activeIds.has(r.id) &&
      !(isFlexibleSubscription && r.id === "prefer_to_order_on_own"),
  );

  if (!randomize) return reasons;

  // Separate "other" — always last
  const other = reasons.find((r) => r.id === "other")!;
  const rest = reasons.filter((r) => r.id !== "other");

  // Fisher-Yates shuffle
  for (let i = rest.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [rest[i], rest[j]] = [rest[j], rest[i]];
  }

  return [...rest, other];
}
